import type {ActionResponse, Department, DepartmentResponse, Document, Fond, HierarchyTree, Rec, RecordCreateParams, RecordHierarchyTreeNode, RecordHolding, RecordRevision, RecordSearchParams, TypeOrIdentifiedStub, UUID} from 'src/typings';
import type {ModalDialogService} from 'shared/modal-dialogs';
import {Kind, SearchType, type Subkind} from 'shared/constants';
import type {FinishedResponseInteractionService, ReloaderService, WalkerService} from 'shared/services';
import type {RecordHoldingDataService} from 'src/features/record/record-holding.data-service';
import type {SearchSelectionModalModel} from 'src/modals/search-selection-modal/types';
import type {RecordEditationModalModel} from 'src/modals/record-editation-modal/types';
import type {RecordRow} from 'src/features/record-grid/lib/types';
import type RecordDataService from './record.data-service';
import type ExemplarService from './exemplar.service';
import {isDocument} from 'shared/utils/types-utils';
import {catchManualInterruption, isInterruptedActionResponse} from 'src/modals/modal-utils';
import {getRecordPath, getRecordUrlId, transferify} from 'shared/utils/data-service-utils';
import {exists} from 'shared/utils/custom-utils';

export default class RecordService {
    public static readonly serviceName = 'recordService';

    /*@ngInject*/
    constructor(private recordDataService: RecordDataService,
                private recordHoldingDataService: RecordHoldingDataService,
                private exemplarService: ExemplarService,
                private currentDepartment: Department,
                private walker: WalkerService,
                private reloader: ReloaderService,
                private finishedResponseInteractionService: FinishedResponseInteractionService,
                private modalDialogService: ModalDialogService) {
    }

    public async getById(recordId: UUID): Promise<Rec | null> {
        try {
            return await this.recordDataService.getById(recordId);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            return null;
        }
    }


    public async findRecordOrCreateRecordWithHolding(staticSearchParams: RecordSearchParams, createParams: RecordCreateParams): Promise<Rec> {
        const modalModel: SearchSelectionModalModel = {
            staticSearchParams: transferify(staticSearchParams),
            createNewValueFn: () => this.createRecordEditation(createParams)
        };
        const record = await this.modalDialogService.openModalWindowAndGetPayload('searchSelection', modalModel) as Rec;
        await this.createRecordHoldingOnCurrentCtx(record);
        return record;
    }

    public async createRecordEditation(createParams: RecordCreateParams, existingRecord?: TypeOrIdentifiedStub<Rec | RecordRow>): Promise<Rec> {
        if (!createParams.kind && !createParams.subkind && !createParams.fond) {
            // TODO: await fond selection dialog
            throw new Error('Fond selection dialog is not implemented yet');
        }

        const recordModalModel: RecordEditationModalModel = {
            editationParams: {
                subkind: createParams.subkind,
                fond: createParams.fond,
                rootFond: createParams.rootFond,
                record: existingRecord,
                initialValues: createParams.initialValues
            },
            focusedFieldTypeId: createParams.focusedFieldTypeId
        };

        const recordLabeledIdentified = await this.modalDialogService.openModalWindowAndGetPayload('recordEditation', recordModalModel);

        try {
            return await this.recordDataService.getById(recordLabeledIdentified.id);
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async createRecordHoldingAndAddExemplarsThrowingToModal(subkind?: Subkind, fond?: Fond, recordPresearch: boolean = true): Promise<void> {
        if (!subkind) {
            // TODO: await fond selection dialog
            throw new Error('Fond selection dialog is not implemented yet');
        }

        let record: Rec;
        const createParams: RecordCreateParams = {
            kind: [Kind.KIND_RECORD],
            subkind,
            fond,
            rootFond: fond
        };

        if (recordPresearch) {
            const searchParams: RecordSearchParams = {
                type: SearchType.TYPE_SEARCH_SELECTION,
                kind: [Kind.KIND_RECORD],
                subkind,
                ...exists(fond) ? {rootFond: [fond],fond: [fond]} : {}
            };
            record = await this.findRecordOrCreateRecordWithHolding(searchParams, createParams);
        } else {
            record = await this.createRecordEditation(createParams);
        }

        try {
            await catchManualInterruption(() => this.createExemplarIfExemplarable(record));
            await this.walker.newPage(getRecordPath(getRecordUrlId(record)));

        } catch (exceptionResponse) {
            if (!isInterruptedActionResponse(exceptionResponse)) {
                await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            }
        }
    }

    public async copyRecordToSpecifiedFondAndAddRecordHoldingThrowingToModal(source: Rec): Promise<void> {
        try {
            const {savedObject: record} = await this.recordDataService.copyRecord(source);
            await this.walker.newPage(getRecordPath(getRecordUrlId(record)));

        } catch (exceptionResponse) {
            if (!isInterruptedActionResponse(exceptionResponse)) {
                await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            }
        }
    }

    private async createRecordHoldingOnCurrentCtx(record: Rec): Promise<RecordHolding> {
        return this.createRecordHolding(record, this.currentDepartment);
    }

    public async createRecordHolding(record: Rec, targetDepartment?: DepartmentResponse): Promise<RecordHolding> {
        try {
            const response = await this.recordHoldingDataService.createRecordHolding(record, targetDepartment);
            return response.savedObject;
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }

    private async createExemplarIfExemplarable(record: Rec): Promise<ActionResponse | null> {
        if (isDocument(record) && record.fond.withExemplars) {
            return this.exemplarService.createEmptyExemplar(record, false);
        }
        return null;
    }

    public async updateWithExternalRecordAndReloadThrowingToToast(record: Rec, modalModel: SearchSelectionModalModel): Promise<void> {
        try {
            const externalRecord: Rec = await this.modalDialogService.openModalWindowAndGetPayload('searchSelection', modalModel) as Rec;
            const actionResponse: ActionResponse = await this.recordDataService.mergeRecords(record, [externalRecord], true);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            await this.reloader.reloadRoute();
        } catch (exceptionResponse) {
            if (!isInterruptedActionResponse(exceptionResponse)) {
                this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            }
        }
    }

    public async mergeRecordsAndReload(target: Rec, sources: Rec[]): Promise<void> {
        try {
            const actionResponse: ActionResponse = await this.recordDataService.mergeRecords(target, sources);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            this.reloader.reloadPage(10);
        } catch (exceptionResponse) {
            if (!isInterruptedActionResponse(exceptionResponse)) {
                throw exceptionResponse;
            }
        }
    }

    public async getAllRecordRevisions(record: Rec): Promise<RecordRevision[]> {
        try {
            return await this.recordDataService.queryRecordRevisions(record);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async sendTemplatedMail(record: Rec): Promise<boolean> {
        try {
            const actionResponse = await this.recordDataService.sendTemplatedMail(record.id);
            await this.finishedResponseInteractionService.showSuccessResponseInToastIfPossible(actionResponse);
            return true;
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
        }

        return false;
    }

    public async getAllRecordHoldings(record: Rec): Promise<RecordHolding[]> {
        try {
            return await this.recordHoldingDataService.queryRecordHoldings({record});
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async getAssignableDepartments(): Promise<DepartmentResponse[]> {
        try {
            return await this.recordHoldingDataService.getAssignableDepartments();
        } catch (errorException) {
            this.finishedResponseInteractionService.showFailedResponseInToast(errorException);
            return [];
        }
    }

    public async getAllRecordParts(record: Rec, pageNumber: number, pageSize: number): Promise<Document[]> {
        try {
            return await this.recordDataService.getRecordParts(record.id, pageNumber, pageSize);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async getAllRecordArticles(record: Rec, pageNumber: number, pageSize: number): Promise<Document[]> {
        try {
            return await this.recordDataService.getRecordArticles(record.id, pageNumber, pageSize);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async getRecordHierarchyTrees(record: Rec): Promise<HierarchyTree<UUID, RecordHierarchyTreeNode>[]> {
        try {
            return await this.recordDataService.getRecordHierarchyTrees(record.id);
        } catch (exceptionResponse) {
            this.finishedResponseInteractionService.showFailedResponseInToast(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async deleteRecordHolding(recordHolding: RecordHolding, goToHomepageInsteadOfReload: boolean | null = null): Promise<void> {
        try {
            await this.recordHoldingDataService.deleteRecordHolding(recordHolding);

            if (exists(goToHomepageInsteadOfReload)) {
                if (goToHomepageInsteadOfReload) {
                    await this.walker.newPage('/');
                } else {
                    await this.reloader.reloadRoute();
                }
            }
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }

    public async discardRecordHolding(recordHolding: RecordHolding): Promise<void> {
        try {
            await this.recordHoldingDataService.discardRecordHolding(recordHolding);
            await this.walker.newPage('/');
        } catch (exceptionResponse) {
            await this.finishedResponseInteractionService.showFailedResponseInModalWindow(exceptionResponse);
            throw exceptionResponse;
        }
    }
}
